import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';

import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { RegistrationStatisticsService } from 'services/api/registration-statistics';

type RegistrationDay = {
  date: string;
  count: number;
  games: GameCategoryStats;
  isCurrentDay: boolean;
};

type GameCategoryStats = {
  freePlay: number;
  cash: number;
  tournament: number;
  league: number;
  total: number;
};

type RegistrationStatsResponse = {
  timezone: 'Asia/Kolkata';
  days: number;
  generatedAt: string;
  rows: RegistrationDay[];
};

type SortDirection = 'asc' | 'desc';

const DEFAULT_DAYS = 30;
const MIN_DAYS = 1;
const MAX_DAYS = 365;

const dayFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

const timeFormatter = new Intl.DateTimeFormat('ru-RU', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Kolkata',
});

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  title: {
    margin: 0,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  daysField: {
    width: 170,
  },
  searchField: {
    width: 240,
  },
  applyButton: {
    minHeight: 40,
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  table: {
    minWidth: 980,
  },
  headerCell: {
    fontWeight: 700,
  },
  currentRow: {
    backgroundColor: 'rgba(0, 188, 212, 0.08)',
  },
  countCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  numberCell: {
    whiteSpace: 'nowrap',
  },
  stateCell: {
    height: 140,
    textAlign: 'center',
  },
  error: {
    color: theme.palette.error.main,
  },
  pagination: {
    overflow: 'visible',
  },
}));

function formatDay(row: RegistrationDay, generatedAt: string) {
  const label = dayFormatter.format(new Date(`${row.date}T12:00:00.000Z`));

  if (!row.isCurrentDay) return label;

  return `${label}, до ${timeFormatter.format(new Date(generatedAt))} IST`;
}

export default function RegistrationStatistics() {
  const classes = useStyles();
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [daysInput, setDaysInput] = useState(String(DEFAULT_DAYS));
  const [rows, setRows] = useState<RegistrationDay[]>([]);
  const [generatedAt, setGeneratedAt] = useState('');
  const [search, setSearch] = useState('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await RegistrationStatisticsService.getDaily(days);
      const data = response.data as RegistrationStatsResponse;
      setRows(data.rows || []);
      setGeneratedAt(data.generatedAt);
      setPage(0);
    } catch (requestError) {
      setError('Не удалось загрузить статистику регистраций');
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const displayedRows = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('ru-RU');
    const filtered = normalizedSearch
      ? rows.filter((row) =>
          formatDay(row, generatedAt).toLocaleLowerCase('ru-RU').includes(normalizedSearch),
        )
      : rows;

    return [...filtered].sort((left, right) => {
      const comparison = left.date.localeCompare(right.date);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [generatedAt, rows, search, sortDirection]);

  const visibleRows = displayedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalRegistrations = rows.reduce((sum, row) => sum + row.count, 0);
  const totalGames = rows.reduce((sum, row) => sum + (row.games?.total || 0), 0);

  const applyDays = (event: FormEvent) => {
    event.preventDefault();
    const nextDays = Number(daysInput);

    if (!Number.isInteger(nextDays) || nextDays < MIN_DAYS || nextDays > MAX_DAYS) {
      setError(`Количество дней должно быть от ${MIN_DAYS} до ${MAX_DAYS}`);
      return;
    }

    setError('');
    setDays(nextDays);
  };

  return (
    <GridContainer>
      {/* @ts-ignore */}
      <GridItem xs={12}>
        <Card>
          <CardHeader color="success">
            <div className={classes.header}>
              <h4 className={classes.title}>Регистрации и игры реальных пользователей</h4>
              <Tooltip title="Обновить">
                <span>
                  <IconButton
                    aria-label="Обновить статистику"
                    color="inherit"
                    disabled={isLoading}
                    onClick={loadData}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </CardHeader>
          <CardBody>
            <div className={classes.toolbar}>
              <form className={classes.controls} onSubmit={applyDays}>
                <TextField
                  className={classes.daysField}
                  label="Количество дней"
                  type="number"
                  variant="outlined"
                  size="small"
                  value={daysInput}
                  inputProps={{ min: MIN_DAYS, max: MAX_DAYS }}
                  onChange={(event) => setDaysInput(event.target.value)}
                />
                <Button
                  className={classes.applyButton}
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isLoading}
                >
                  Показать
                </Button>
              </form>
              <TextField
                className={classes.searchField}
                label="Фильтр по дате"
                variant="outlined"
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </div>

            <div className={classes.summary}>
              <span>Период: {days} дн.</span>
              <span>Всего регистраций: {totalRegistrations}</span>
              <span>Всего игр: {totalGames}</span>
              {generatedAt && (
                <span>Обновлено: {timeFormatter.format(new Date(generatedAt))} IST</span>
              )}
            </div>

            <TableContainer>
              <Table
                className={classes.table}
                aria-label="Регистрации и игры реальных пользователей по дням"
              >
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={() =>
                          setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
                        }
                      >
                        Дата, IST
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className={classes.headerCell}>Регистрации</TableCell>
                    <TableCell className={classes.headerCell}>Free Play</TableCell>
                    <TableCell className={classes.headerCell}>Игры на деньги</TableCell>
                    <TableCell className={classes.headerCell}>Турниры</TableCell>
                    <TableCell className={classes.headerCell}>Лиги</TableCell>
                    <TableCell className={classes.headerCell}>Всего игр</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell className={classes.stateCell} colSpan={7}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && error && (
                    <TableRow>
                      <TableCell className={classes.stateCell} colSpan={7}>
                        <Typography className={classes.error}>{error}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && !error && visibleRows.length === 0 && (
                    <TableRow>
                      <TableCell className={classes.stateCell} colSpan={7}>
                        Нет данных
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading &&
                    !error &&
                    visibleRows.map((row) => (
                      <TableRow
                        className={row.isCurrentDay ? classes.currentRow : undefined}
                        hover
                        key={row.date}
                      >
                        <TableCell>{formatDay(row, generatedAt)}</TableCell>
                        <TableCell className={classes.countCell}>{row.count}</TableCell>
                        <TableCell className={classes.numberCell}>
                          {row.games?.freePlay || 0}
                        </TableCell>
                        <TableCell className={classes.numberCell}>{row.games?.cash || 0}</TableCell>
                        <TableCell className={classes.numberCell}>
                          {row.games?.tournament || 0}
                        </TableCell>
                        <TableCell className={classes.numberCell}>
                          {row.games?.league || 0}
                        </TableCell>
                        <TableCell className={classes.countCell}>{row.games?.total || 0}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              className={classes.pagination}
              component="div"
              count={displayedRows.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 30, 60, 100]}
              labelRowsPerPage="Строк на странице"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
